/**
 * How a tie is followed from the head that starts it to the head that ends it.
 *
 * A tie is stored as two flags, one on each head, and nothing in the document says which two
 * heads they are. Something has to pair them, and it cannot be done by a voice's index in its
 * bar: a voice belongs to its bar, so the same line of music stands at one index in this bar
 * and at another in the next, and a file may tie a note of one voice into a note of another.
 * So a tie is followed by what it is -- the same pitch going on, however it is spelled --
 * and not by where it is kept:
 *
 * - a head that ends a tie takes up a tie of its own pitch, anywhere in the part, whose note
 *   has ended by the time this one begins;
 * - it takes the one that ends exactly where it begins before one that ended earlier, and
 *   among those the one in its own line of music, then one on its own staff;
 * - a tie may pass over other notes on its way -- a broken chord held into the chord it
 *   spells, a grace tied over the next grace into the note they lean on -- but not over its
 *   own pitch struck again: once the pitch has been sounded anew the old tie has nothing left
 *   to hold.
 *
 * The rule is here once. Validation asks it whether a tie is whole, playback asks it which
 * two notes are one sound, and the editor asks it which head a tie it is taking off ends on,
 * so the three cannot come to disagree about the same score.
 */

import type { EventId, MeasureId } from "./ids";
import { midiOf } from "./pitch";
import type { NotePitch } from "./pitch";
import { capacityOf, eventOnsets, voiceTicks } from "./query";
import { playOrder } from "./repeats";
import { lineNames } from "./standing";
import type { Part, Score } from "./types";

/**
 * How a pitch is named for the purpose of matching a tie to the head it ties to.
 *
 * By what it sounds and not by how it is spelled: a G sharp held over a change of key is
 * written on as an A flat, and the two are one key held down.
 */
export function tieKey(pitch: NotePitch): string {
  return `${midiOf(pitch)}`;
}

/** A head as the rule sees it: what it is, when it sounds, and where it is written. */
export interface TiedHead {
  readonly key: string;
  /** When the head begins and when its written note ends, on the clock its reader keeps. */
  readonly at: number;
  readonly ends: number;
  readonly staff: number;
  /** The line of music it is in, by the name that line keeps from bar to bar. */
  readonly line: string;
}

/** A tie waiting for the head that ends it. */
interface Waiting<T> {
  readonly head: TiedHead;
  readonly held: T;
  /** When the pitch was first struck again after the tie's note ended. */
  readonly cut: number | null;
}

/**
 * The ties of one part that are open as its music is read.
 *
 * Heads are given in the order they begin. A reader that plays the piece gives them in the
 * order played, repeats and all, so that a note before a second ending is tied into it the
 * second time round.
 */
export interface TieLedger<T> {
  /** Take up the tie this head ends, answering with what it was opened with. */
  claim(head: TiedHead): T | undefined;
  /** Open the tie this head starts. */
  open(head: TiedHead, held: T): void;
  /** Note that a pitch was sounded anew, which no earlier tie of it reaches past. */
  strike(key: string, at: number): void;
  /** What was opened with every tie nothing has ended. */
  left(): readonly T[];
}

/** How well a waiting tie fits the head that would end it. */
function fit(tie: TiedHead, head: TiedHead): number {
  const staff = tie.staff === head.staff;
  return (
    (tie.ends === head.at ? 4 : 0) + (staff && tie.line === head.line ? 2 : 0) + (staff ? 1 : 0)
  );
}

export function tieLedger<T>(): TieLedger<T> {
  const waiting: Waiting<T>[] = [];
  return {
    claim(head) {
      let best = -1;
      let rank = -1;
      waiting.forEach((tie, index) => {
        if (tie.head.key !== head.key || tie.head.ends > head.at) return;
        if (tie.cut !== null && tie.cut < head.at) return;
        const found = fit(tie.head, head);
        if (found > rank) {
          best = index;
          rank = found;
        }
      });
      return best < 0 ? undefined : waiting.splice(best, 1)[0]?.held;
    },
    open(head, held) {
      waiting.push({ head, held, cut: null });
    },
    strike(key, at) {
      waiting.forEach((tie, index) => {
        if (tie.head.key !== key || tie.cut !== null || tie.head.ends >= at) return;
        waiting[index] = { ...tie, cut: at };
      });
    },
    left() {
      return waiting.map((tie) => tie.held);
    },
  };
}

/** What is read out of several voices in the order it begins; a moment keeps the order given. */
export function inTime<T extends { readonly start: number }>(reached: readonly T[]): readonly T[] {
  return [...reached].sort((one, other) => one.start - other.start);
}

/**
 * How long a bar of a part is, in ticks.
 *
 * Whichever is longer, its metre or the most any one voice actually holds. An overfull bar is
 * a real document -- it is one of the things validation reports -- and measuring it by its
 * metre alone would place its last notes after the start of the bar that follows, so that
 * everything leading out of it looked as though it ran backwards.
 */
function barTicks(score: Score, part: Part, index: number): number {
  const global = score.measures[index];
  const voices = global === undefined ? [] : (part.measures[global.id]?.voices ?? []);
  const held = voices.reduce((most, voice) => Math.max(most, voiceTicks(voice)), 0);
  return Math.max(capacityOf(score, index), held);
}

/** Where each bar of a part begins as written, in ticks from the start of the piece. */
export function barStarts(score: Score, part: Part): readonly number[] {
  let start = 0;
  return [...score.measures.keys()].map((index) => {
    const begins = start;
    start += barTicks(score, part, index);
    return begins;
  });
}

/** One head of a part, by the event it is in and where that event is kept. */
export interface HeadAt {
  readonly eventId: EventId;
  /** Which head of the event's chord it is. */
  readonly pitch: number;
  readonly measureId: MeasureId;
  readonly measureIndex: number;
  readonly voiceIndex: number;
}

/** The ties of a part: which join, and which are left with one end. */
export interface Ties {
  /**
   * The heads each whole tie ends on, by the {@link headName} of the head it starts on.
   * More than one where a repeat brings the note round to another ending: the note before a
   * first- and second-time bar is tied into both.
   */
  readonly into: ReadonlyMap<string, readonly HeadAt[]>;
  /** The heads each whole tie starts on, by the name of the head it ends on. */
  readonly from: ReadonlyMap<string, readonly HeadAt[]>;
  /** Heads that start a tie nothing ends, as written or on any time through. */
  readonly unresolved: readonly HeadAt[];
  /** Heads that end a tie nothing started, as written or on any time through. */
  readonly unstarted: readonly HeadAt[];
}

/** The name {@link Ties} keeps a head under. */
export function headName(eventId: EventId, pitch: number): string {
  return `${eventId}#${pitch}`;
}

/**
 * Follow every tie of a part, through the bars as they are written and as they are played.
 *
 * A repeat brings a note round again, and what it is tied to may be different the second
 * time: the note before a first-time bar is tied into that bar and then into the second-time
 * bar, and the last note inside a repeat is tied out of it only when the repeat is over. The
 * written order sees none of that, and the played order does not see a tie drawn from the
 * end of a first-time bar into the second-time bar beside it, nor any tie in a bar no pass
 * reaches. So both are read, and a tie is whole when its two heads meet in either: it is
 * reported only when it joins on the page nowhere and on no time through.
 *
 * An ossia is what the player may do instead of the bar under it, so its ties are followed
 * among its own notes: a pitch struck in it must not cut a tie held in the line it stands
 * over, nor end one. A cue is followed with the part, because a note held into the small
 * notes that finish its bar is tied to them on the page whether or not they sound.
 *
 * `rewritten` stands in for the heads of the events it names, which is how an edit asks what
 * the ties would be before it is made.
 */
export function tiesOf(
  score: Score,
  part: Part,
  rewritten: ReadonlyMap<EventId, readonly NotePitch[]> = new Map(),
): Ties {
  const into = new Map<string, HeadAt[]>();
  const from = new Map<string, HeadAt[]>();
  // Every head a reading left with one end, until the other reading gives it the other.
  const open = new Map<string, HeadAt>();
  const asked = new Map<string, HeadAt>();

  const lines = lineNames(score, part);

  const follow = (order: readonly number[]): void => {
    const ledgers = new Map<boolean, TieLedger<HeadAt>>();
    let clock = 0;
    for (const measureIndex of order) {
      const barStart = clock;
      clock += barTicks(score, part, measureIndex);
      const global = score.measures[measureIndex];
      const measure = global === undefined ? undefined : part.measures[global.id];
      if (global === undefined || measure === undefined) continue;
      const reached = measure.voices.flatMap((voice, voiceIndex) => {
        const within = eventOnsets(voice);
        const length = voiceTicks(voice);
        const line = lines.get(global.id)?.[voiceIndex] ?? `${voiceIndex}`;
        return voice.events.map((event, place) => ({
          event,
          voice,
          voiceIndex,
          line,
          start: barStart + (within[place] ?? 0),
          ends: barStart + (within[place + 1] ?? length),
        }));
      });

      for (const { event, voice, voiceIndex, line, start, ends } of inTime(reached)) {
        if (event.kind !== "note") continue;
        const ledger = ledgers.get(voice.ossia) ?? tieLedger<HeadAt>();
        ledgers.set(voice.ossia, ledger);
        (rewritten.get(event.id) ?? event.pitches).forEach((pitch, index) => {
          const head: TiedHead = {
            key: tieKey(pitch),
            at: start,
            ends,
            staff: event.staff ?? voice.staff,
            line,
          };
          const here: HeadAt = {
            eventId: event.id,
            pitch: index,
            measureId: global.id,
            measureIndex,
            voiceIndex,
          };
          const name = headName(event.id, index);
          const began = pitch.tieStop ? ledger.claim(head) : undefined;
          if (began === undefined) {
            if (pitch.tieStop) asked.set(name, here);
            ledger.strike(head.key, start);
          } else {
            const begun = headName(began.eventId, began.pitch);
            const known = into.get(begun) ?? [];
            // The same two heads are joined again every time a repeat brings them round.
            if (!known.some((other) => headName(other.eventId, other.pitch) === name)) {
              into.set(begun, [...known, here]);
              from.set(name, [...(from.get(name) ?? []), began]);
            }
          }
          if (pitch.tieStart) ledger.open(head, here);
        });
      }
    }
    for (const ledger of ledgers.values()) {
      for (const head of ledger.left()) open.set(headName(head.eventId, head.pitch), head);
    }
  };

  const written = playOrder(score, false);
  const played = playOrder(score);
  follow(written);
  if (played.length !== written.length || played.some((index, at) => index !== written[at])) {
    follow(played);
  }

  return {
    into,
    from,
    unresolved: [...open].filter(([name]) => !into.has(name)).map(([, head]) => head),
    unstarted: [...asked].filter(([name]) => !from.has(name)).map(([, head]) => head),
  };
}
